import api from "@/utils/api";
import { ApiResponse } from "@/utils/api/ApiProps";

// ===== TYPES =====

export interface DripEmailTemplate {
  id: number;
  uuid: string;
  client_id_fk: number | null;
  template_name: string;
  template_slug: string;
  description: string | null;
  category: 'welcome' | 'nurture' | 'educational' | 're_engagement' | 'promotional' | 'other';
  subject_line: string;
  preheader_text: string | null;
  html_body: string;
  plain_text_body: string | null;
  variables: Array<{
    name: string;
    label: string;
    default: string;
    required: boolean;
    leadField?: string;
  }>;
  thumbnail_url: string | null;
  is_active: boolean;
  is_system_template: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface IntegrationTemplate {
  id: number;
  integration_id_fk: number;
  element_name: string;
  channel: 'whatsapp' | 'sms' | 'email';
  status: 'APPROVED' | 'PENDING' | 'REJECTED';
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  language: string;
  body: string | null;
  header: string | null;
  footer: string | null;
  buttons: any[];
  parameters: string[] | Array<{ type: string; text: string }>;
  parameter_mapping?: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

// Auto-enrollment configuration types
export interface EnrollmentCondition {
  field: string; // 'source', 'status', 'city', 'state', etc.
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains';
  value: string;
}

export interface AutoEnrollmentConfig {
  enabled: boolean;
  trigger_type: 'new_lead' | 'status_change' | 'manual';
  conditions: EnrollmentCondition[];
  logic: 'AND' | 'OR'; // how to combine conditions
}

// Status exit configuration types
export interface StatusExitCondition {
  value: string; // status value to watch for
  send_template: boolean;
  template_type?: 'email' | 'whatsapp';
  template_id?: number; // for email
  integration_template_id?: number; // for whatsapp
  template_subject?: string; // for email
  parameter_mapping?: Record<string, string>; // for whatsapp
}

export interface StatusExitConfig {
  enabled: boolean;
  monitor_field: 'status'; // currently only status, but could expand
  exit_conditions: StatusExitCondition[];
}

export interface DripCampaignStep {
  id: number;
  uuid: string;
  campaign_id_fk: number;
  template_id_fk: number | null;
  integration_template_id_fk: number | null;
  step_name: string;
  step_order: number;
  delay_value: number;
  delay_unit: 'minutes' | 'hours' | 'days' | 'weeks';
  delay_from: 'enrollment' | 'previous_step';
  conditions: any;
  custom_subject_line: string | null;
  parameter_mapping: Record<string, string>;
  status_exit_config: StatusExitConfig | null; // NEW: Exit campaign on status change
  is_active: boolean;
  total_sent: number;
  total_failed: number;
  template?: DripEmailTemplate;
  integrationTemplate?: IntegrationTemplate;
  created_at: string;
  updated_at: string;
}

export interface DripCampaign {
  id: number;
  uuid: string;
  client_id_fk: number;
  campaign_name: string;
  campaign_slug: string;
  description: string | null;
  goal: string | null;
  channel_type: 'email' | 'whatsapp' | 'sms';
  enrollment_type: 'manual' | 'auto_trigger';
  auto_enrollment_trigger: any; // Legacy field for status change triggers
  auto_enrollment_config: AutoEnrollmentConfig | null; // NEW: Advanced enrollment triggers
  allow_re_enrollment: boolean;
  send_time_preference: 'immediate' | 'business_hours' | 'optimal';
  business_hours_start: string;
  business_hours_end: string;
  timezone: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  is_active: boolean;
  total_enrolled: number;
  total_completed: number;
  total_unsubscribed: number;
  tags: string[];
  steps?: DripCampaignStep[];
  created_at: string;
  updated_at: string;
}

export interface DripCampaignEnrollment {
  id: number;
  uuid: string;
  campaign_id_fk: number;
  lead_id_fk: number;
  lead_uuid: string;
  enrollment_source: 'manual' | 'auto_trigger' | 'import';
  enrolled_at: string;
  current_step_order: number;
  status: 'active' | 'paused' | 'completed' | 'unsubscribed' | 'bounced' | 'failed';
  completed_at: string | null;
  unsubscribed_at: string | null;
  emails_sent: number;
  emails_opened: number;
  emails_clicked: number;
  lead?: any;
}

export interface ClientBranding {
  id: number;
  uuid: string;
  client_id_fk: number;
  logo_url: string | null;
  logo_width: number;
  logo_height: number;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  company_name: string | null;
  company_address: string | null;
  support_email: string | null;
  support_phone: string | null;
  website_url: string | null;
  from_email: string | null;
  from_name: string | null;
  email_verified: boolean;
  email_verified_at: string | null;
  social_links: Record<string, string>;
  footer_text: string | null;
  show_unsubscribe_link: boolean;
  unsubscribe_text: string;
  show_address: boolean;
  show_social_links: boolean;
  custom_disclaimer: string | null;
}

// ===== CAMPAIGNS =====

export const getCampaigns = async (filters?: {
  status?: string;
  enrollment_type?: string;
}): Promise<ApiResponse<DripCampaign[]>> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.enrollment_type) params.append('enrollment_type', filters.enrollment_type);

  const url = params.toString() ? `/drip/campaigns?${params}` : '/drip/campaigns';
  return api.get<DripCampaign[]>(url);
};

export const getCampaignById = async (
  campaignId: number | string
): Promise<ApiResponse<DripCampaign>> => {
  return api.get<DripCampaign>(`/drip/campaigns/${campaignId}`);
};

export const createCampaign = async (
  data: Partial<DripCampaign>
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: '/drip/campaigns',
    method: api.Method.POST,
    body: data,
  });
};

export const updateCampaign = async (
  campaignId: number,
  data: Partial<DripCampaign>
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: `/drip/campaigns/${campaignId}`,
    method: api.Method.PUT,
    body: data,
  });
};

export const deleteCampaign = async (
  campaignId: number
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/campaigns/${campaignId}`,
    method: api.Method.DELETE,
  });
};

export const activateCampaign = async (
  campaignId: number
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: `/drip/campaigns/${campaignId}/activate`,
    method: api.Method.POST,
  });
};

export const pauseCampaign = async (
  campaignId: number
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: `/drip/campaigns/${campaignId}/pause`,
    method: api.Method.POST,
  });
};

export const getCampaignStats = async (
  campaignId: number
): Promise<ApiResponse<any>> => {
  return api.get(`/drip/campaigns/${campaignId}/stats`);
};

// ===== CAMPAIGN STEPS =====

export const addCampaignStep = async (
  campaignId: number,
  data: Partial<DripCampaignStep>
): Promise<ApiResponse<DripCampaignStep>> => {
  return api.req<DripCampaignStep>({
    url: `/drip/campaigns/${campaignId}/steps`,
    method: api.Method.POST,
    body: data,
  });
};

export const updateCampaignStep = async (
  campaignId: number,
  stepId: number,
  data: Partial<DripCampaignStep>
): Promise<ApiResponse<DripCampaignStep>> => {
  return api.req<DripCampaignStep>({
    url: `/drip/campaigns/${campaignId}/steps/${stepId}`,
    method: api.Method.PUT,
    body: data,
  });
};

export const deleteCampaignStep = async (
  campaignId: number,
  stepId: number
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/campaigns/${campaignId}/steps/${stepId}`,
    method: api.Method.DELETE,
  });
};

// ===== ENROLLMENTS =====

export const enrollLead = async (
  campaignId: number,
  leadId: number
): Promise<ApiResponse<DripCampaignEnrollment>> => {
  return api.req<DripCampaignEnrollment>({
    url: `/drip/campaigns/${campaignId}/enroll`,
    method: api.Method.POST,
    body: { leadId },
  });
};

export const getCampaignEnrollments = async (
  campaignId: number,
  filters?: { status?: string }
): Promise<ApiResponse<DripCampaignEnrollment[]>> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);

  const url = params.toString()
    ? `/drip/campaigns/${campaignId}/enrollments?${params}`
    : `/drip/campaigns/${campaignId}/enrollments`;

  return api.get<DripCampaignEnrollment[]>(url);
};

export const unenrollLead = async (
  enrollmentId: number,
  reason?: string
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/enrollments/${enrollmentId}`,
    method: api.Method.DELETE,
    body: { reason },
  });
};

// ===== TEMPLATES =====

export const getTemplates = async (filters?: {
  category?: string;
}): Promise<ApiResponse<DripEmailTemplate[]>> => {
  const params = new URLSearchParams();
  if (filters?.category) params.append('category', filters.category);

  const url = params.toString() ? `/drip/templates?${params}` : '/drip/templates';
  return api.get<DripEmailTemplate[]>(url);
};

export const getTemplateById = async (
  templateId: number
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.get<DripEmailTemplate>(`/drip/templates/${templateId}`);
};

export const createTemplate = async (
  data: Partial<DripEmailTemplate>
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.req<DripEmailTemplate>({
    url: '/drip/templates',
    method: api.Method.POST,
    body: data,
  });
};

export const updateTemplate = async (
  templateId: number,
  data: Partial<DripEmailTemplate>
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.req<DripEmailTemplate>({
    url: `/drip/templates/${templateId}`,
    method: api.Method.PUT,
    body: data,
  });
};

export const deleteTemplate = async (
  templateId: number
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/templates/${templateId}`,
    method: api.Method.DELETE,
  });
};

// ===== INTEGRATION TEMPLATES (WhatsApp/SMS) =====

export const getIntegrationTemplates = async (filters?: {
  channel?: 'whatsapp' | 'sms' | 'email';
  status?: 'APPROVED' | 'PENDING' | 'REJECTED';
}): Promise<ApiResponse<IntegrationTemplate[]> & { provider?: string }> => {
  // Unified endpoint — auto-detects AiSensy or WATI for the client
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);

  const url = params.toString() ? `/integrations/whatsapp/templates?${params}` : '/integrations/whatsapp/templates';
  return api.get<IntegrationTemplate[]>(url) as Promise<ApiResponse<IntegrationTemplate[]> & { provider?: string }>;
};

export const cloneTemplate = async (
  templateId: number
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.req<DripEmailTemplate>({
    url: `/drip/templates/${templateId}/clone`,
    method: api.Method.POST,
  });
};

export const previewTemplate = async (
  templateId: number,
  sampleData: Record<string, string>
): Promise<ApiResponse<{
  subject: string;
  html: string;
  plainText: string;
  variables: any[];
}>> => {
  return api.req<{
    subject: string;
    html: string;
    plainText: string;
    variables: any[];
  }>({
    url: `/drip/templates/${templateId}/preview`,
    method: api.Method.POST,
    body: sampleData,
  });
};

// ===== CLIENT BRANDING =====

export const getClientBranding = async (): Promise<ApiResponse<ClientBranding>> => {
  return api.get<ClientBranding>('/drip/branding');
};

export const updateClientBranding = async (
  data: Partial<ClientBranding>
): Promise<ApiResponse<ClientBranding>> => {
  return api.req<ClientBranding>({
    url: '/drip/branding',
    method: api.Method.PUT,
    body: data,
  });
};

export const verifyBrandingEmail = async (): Promise<ApiResponse<{
  success: boolean;
  message: string;
  email: string;
}>> => {
  return api.req<{
    success: boolean;
    message: string;
    email: string;
  }>({
    url: '/drip/branding/verify-email',
    method: api.Method.POST,
  });
};

export const checkEmailVerificationStatus = async (): Promise<ApiResponse<{
  success: boolean;
  verified: boolean;
  status: string;
  message: string;
  email: string;
  verifiedAt: string | null;
}>> => {
  return api.get<{
    success: boolean;
    verified: boolean;
    status: string;
    message: string;
    email: string;
    verifiedAt: string | null;
  }>('/drip/branding/verify-email/status');
};

// ===== STATUS OPTIONS =====

export interface StatusOption {
  opt_id: number;
  uuid: string;
  opt_value: string;
  opt_name: string;
}

export const getStatusOptions = async (): Promise<ApiResponse<StatusOption[]>> => {
  return api.get<StatusOption[]>('/integrations/crm/status-options');
};
